import React from "react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";

import ContentItem from "components/common/InfoPopup/ContentItem";
import ContentSection from "components/common/InfoPopup/ContentSection";
import { FORMAT_DATE } from "constants/time";

export default function TransportDetails({ transport }) {
    const { t } = useTranslation();

    const availableFrom = transport.available_from ? new Date(transport.available_from) : undefined;
    const availableTo = transport.available_to ? new Date(transport.available_to) : undefined;
    const hasAvailableDates = availableFrom || availableTo;

    const loadingRadius = transport.loading_radius;
    const unloadingRadius = transport.unloading_radius;
    const hasRoute = transport.loading_address || transport.unloading_address || loadingRadius > 0 || unloadingRadius > 0;

    const formatAddress = (address) => {
        if (!address) return null;
        const fullAddress = address.full_address || '';
        const countryCode = address.country_code || '';
        return fullAddress || countryCode ? `${fullAddress} ${countryCode}`.trim() : null;
    };
    const length = parseFloat(transport.length);
    const width = parseFloat(transport.width);
    const height = parseFloat(transport.height);
    const volume = parseFloat(transport.volume);
    const carryingCapacity = parseFloat(transport.carrying_capacity);
    const isLength = length >= 0;
    const isWidth = width >= 0;
    const isHeight = height >= 0;
    const isVolume = volume >= 0;
    const isCapacity = carryingCapacity >= 0;
    const isMeasure = isLength || isWidth || isHeight || isVolume || isCapacity;

    return (
        <>
            {hasAvailableDates && (
                <ContentSection title={t("transportSearch.availableDates")}>
                    <ContentItem
                        value={
                            availableFrom && availableTo
                                ? `${format(availableFrom, FORMAT_DATE)} - ${format(availableTo, FORMAT_DATE)}`
                                : availableFrom
                                    ? format(availableFrom, FORMAT_DATE)
                                    : format(availableTo, FORMAT_DATE)
                        }
                    />
                </ContentSection>
            )}
            {hasRoute && (
                <ContentSection title={t("transportFields.itinerary_section.heading")} withBorder={hasAvailableDates}>
                    <li style={{ flexBasis: '100%' }}>
                        <ul style={{ display: 'flex' }}>
                            <ContentItem
                                title={t("cargoFields.itinerary_section.uploading")}
                                value={formatAddress(transport.loading_address) || '-'}
                                isFullWidth={!(loadingRadius > 0)}
                            />
                            {loadingRadius > 0 && (
                                <ContentItem
                                    title={t("transportFields.itinerary_section.availability")}
                                    value={`${loadingRadius} ${t("transportFields.itinerary_section.km")}`}
                                />
                            )}
                        </ul>
                    </li>
                    <li style={{ flexBasis: '100%' }}>
                        <ul style={{ display: 'flex' }}>
                            <ContentItem
                                title={t("cargoFields.itinerary_section.unloading")}
                                value={formatAddress(transport.unloading_address) || '-'}
                                isFullWidth={!(unloadingRadius > 0)}
                            />
                            {unloadingRadius > 0 && (
                                <ContentItem
                                    title={t("transportFields.itinerary_section.availability")}
                                    value={`${unloadingRadius} ${t("transportFields.itinerary_section.km")}`}
                                />
                            )}
                        </ul>
                    </li>
                </ContentSection>
            )}
            {
                <ContentSection title={t("transportFields.body_section.heading")} withBorder={hasAvailableDates || hasRoute}>
                    {(transport.transport_bodytypes?.length > 0 || transport.transport_bodytype || transport.transport_bodytype_additional) && (
                        <ContentItem
                            title={t("transportFields.body_section.body")}
                            value={[
                                // Handle array of body types
                                ...(transport.transport_bodytypes?.map((bt) => `- ${bt.value}`) || []),
                                // Fallback for single body type (legacy)
                                ...(transport.transport_bodytype && !transport.transport_bodytypes?.length
                                    ? [`- ${transport.transport_bodytype.value}`]
                                    : []),
                                transport.transport_bodytype_additional &&
                                    `- ${transport.transport_bodytype_additional.value}`,
                            ]}
                        />
                    )}
                    {transport.transports_loading_types && transport.transports_loading_types.length > 0 && (
                        <ContentItem
                            title={t("transportFields.body_section.uploading")}
                            value={transport.transports_loading_types.map(
                                (el) => `- ${el.loading_type.value}`,
                            )}
                        />
                    )}
                    {(Boolean(transport.partial_load || transport.partialLoad) ||
                        Boolean(transport.gidrolift) ||
                        Boolean(transport.conics)) && (
                        <ContentItem
                            title={t("transportFields.body_section.construction")}
                            value={[
                                Boolean(transport.conics) &&
                                    `- ${t("transportFields.body_section.bolsters")}`,
                                Boolean(transport.gidrolift) &&
                                    `- ${t("transportFields.body_section.tailLift")}`,
                                Boolean(transport.partial_load || transport.partialLoad) &&
                                    `- ${t("transportFields.body_section.backloading")}`,
                            ]}
                        />
                    )}
                    {transport.registration_number && (
                        <ContentItem
                            title={t("transportFields.main_section.license")}
                            value={transport.registration_number}
                        />
                    )}
                    {transport.trailer_registration_number && (
                        <ContentItem
                            title={t("transportFields.body_section.if_checkbox_3.licence")}
                            value={transport.trailer_registration_number}
                        />
                    )}
                </ContentSection>
            }
            {isMeasure && (
                <ContentSection
                    title={t("transportFields.vehicle_size_section.heading")}
                    withBorder
                >
                    {isLength && (
                        <ContentItem
                            title={t("transportFields.vehicle_size_section.length")}
                            value={length}
                        />
                    )}
                    {isWidth && (
                        <ContentItem
                            title={t("transportFields.vehicle_size_section.width")}
                            value={width}
                        />
                    )}
                    {isHeight && (
                        <ContentItem
                            title={t("transportFields.vehicle_size_section.height")}
                            value={height}
                        />
                    )}
                    {isVolume && (
                        <ContentItem
                            title={t("transportFields.vehicle_size_section.capacity")}
                            value={volume}
                        />
                    )}
                    {isCapacity && (
                        <ContentItem
                            title={t("transportFields.vehicle_size_section.weight")}
                            value={carryingCapacity}
                        />
                    )}
                </ContentSection>
            )}
            {(transport.transport_manufacturer ||
                transport.transport_manufacture_year) && (
                <ContentSection
                    title={t("transportFields.specification_section.heading")}
                    withBorder
                >
                    {transport.transport_manufacture_year && (
                        <ContentItem
                            title={t("transportFields.specification_section.year")}
                            value={transport.transport_manufacture_year}
                        />
                    )}
                </ContentSection>
            )}
            {Boolean(
                transport.prepayment > 0 ||
                    transport.upload ||
                    transport.direct_contract ||
                    transport.payment_rate_type,
            ) && (
                <ContentSection
                    title={t("exchangeModal.learn_more_section.payment_section")}
                    withBorder
                >
                    {transport.prepayment > 0 && (
                        <ContentItem
                            title={t("transportFields.payment_section.prepayment")}
                            value={`${transport.prepayment}%`}
                        />
                    )}
                    {transport.payment_rate_type && (
                        <ContentItem
                            title={t("transportFields.payment_section.type_payment")}
                            value={transport.payment_rate_type}
                        />
                    )}
                    {(transport.direct_contract ||
                        transport.fuel_prepayment ||
                        transport.upload) && (
                        <ContentItem
                            title={t("cargoFields.payment_section.additional")}
                            value={[
                                transport.direct_contract &&
                                    `- ${t("transportFields.payment_section.contract_box")}`,
                                transport.fuel_prepayment &&
                                    `- ${t("transportFields.payment_section.fuel_box")}`,
                                transport.upload &&
                                    `- ${t("transportFields.payment_section.unloading_box")}`,
                            ]}
                        />
                    )}
                </ContentSection>
            )}
            {transport.notes && (
                <ContentSection title={t("notes")} withBorder>
                    <ContentItem value={transport.notes} isFullWidth />
                </ContentSection>
            )}
        </>
    );
}
